import { ReactNode, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

interface OnboardingCheckProps {
	children: ReactNode;
}

export const OnboardingCheck = ({ children }: OnboardingCheckProps) => {
	const { user, loading } = useAuth();
	const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
	const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);

	useEffect(() => {
		// Only check onboarding status if the user is authenticated
		if (!loading && user) {
			checkOnboardingStatus();
		} else if (!loading && !user) {
			// If not loading and no user, no need to check onboarding
			setIsCheckingOnboarding(false);
		}
	}, [user, loading]);

	const checkOnboardingStatus = async () => {
		try {
			const { data, error } = await supabase
				.from("onboardings")
				.select("completed")
				.eq("id", user?.id)
				.single();

			if (error) {
				console.error("Error checking onboarding status:", error);
				// In case of error, assume onboarding is completed to avoid blocking users
				setOnboardingCompleted(true);
			} else {
				// If we have data, set the completed status
				setOnboardingCompleted(data?.completed);
			}
		} catch (error) {
			console.error("Unexpected error checking onboarding:", error);
			// In case of error, assume onboarding is completed to avoid blocking users
			setOnboardingCompleted(true);
		} finally {
			setIsCheckingOnboarding(false);
		}
	};

	// If still checking onboarding status, show a loading spinner
	if (isCheckingOnboarding) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
			</div>
		);
	}

	// If not completed, redirect to onboarding
	if (user && onboardingCompleted === false) {
		return <Navigate to="/onboarding" replace />;
	}

	// Otherwise, render the children
	return <>{children}</>;
};